import { setActiveSidebarTab } from 'client/actions/actionsManagement'
import { useSidebarVisibilityActionsContext } from 'client/context/SidebarVisibilityContext'
import { useAppDispatch } from 'client/hooks/use-dispatch'
import { usePage, useWebsiteSettings } from 'client/store'
import { setEditingEntity } from 'client/store/management/managementActions'
import { EntityTypeEnum } from 'common/enums/entityTypeEnum'
import { PageLocaleEnum } from 'common/enums/PageLocaleEnum'
import { findEntityByType } from 'common/utils/entityUtils'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutationWithValidation } from 'src/editor/api/hooks/use-api-mutation-with-validation'
import { websitePagesQuery } from 'src/editor/api/queries/website-pages-query'
import {
  UpdateWebsiteDataInterface,
  updateWebsite,
} from 'src/editor/api/website-websites-api'
import { SettingsMenuButton } from 'src/editor/shared/components/settings-menu-buttons/settings-menu-button'
import { TextInputControl } from 'src/editor/shared/settings/controls/text-input-control'
import { LanguageSelector } from 'src/editor/shared/ui-lib/language-selector'
import { getLocalesOptions } from 'src/editor/shared/ui-lib/language-selector/utils/get-locales-options'
import {
  setWebsiteLocale,
  setWebsiteName,
} from 'src/editor/store/website-settings/website-settings-actions'
import {
  getWebsiteId,
  getWebsiteLocale,
  getWebsiteLocales,
  getWebsiteName,
  getWebsiteStyles,
  getWebsiteTheme,
} from 'src/editor/store/website-settings/website-settings-selector'
import { transformWebsiteStylesForApi } from 'src/editor/website/utils/transform-website-styles-for-api'
import { transformWebsiteTheme } from 'src/editor/website/utils/transform-website-theme'
import { GroupTitle } from '../settings/group-title'
import { WebsiteLocalesSelector } from './website-locales-selector'

export const WebsiteSettings = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { showSidebar } = useSidebarVisibilityActionsContext()

  const websiteId = useWebsiteSettings(getWebsiteId) as number
  const websiteName = useWebsiteSettings(getWebsiteName)
  const websiteLocale = useWebsiteSettings(getWebsiteLocale)
  const websiteLocales = useWebsiteSettings(getWebsiteLocales)
  const websiteStyles = useWebsiteSettings(getWebsiteStyles)
  const websiteTheme = useWebsiteSettings(getWebsiteTheme)

  const websiteLogoEntity = usePage(page =>
    findEntityByType(page.entities, EntityTypeEnum.WebsiteLogo),
  )

  const localesOptions = useMemo(() => {
    return getLocalesOptions({
      t,
      localeList: websiteLocales,
      setAdditionalProperties: locale => ({
        nonRemovable: websiteLocale === locale,
      }),
    })
  }, [t, websiteLocale, websiteLocales])

  const { fetcher: fetcherUpdateWebsite, isLoading: isLoadingUpdateWebsite } =
    useMutationWithValidation({
      requestFunction: (data: UpdateWebsiteDataInterface) =>
        updateWebsite(websiteId, data),
      successToastMessage: t(
        'website_settings.sidebar.website_settings.updated_successfully',
      ),
      handleSuccess: (_, [{ defaultLocale }]) =>
        dispatch(setWebsiteLocale(defaultLocale as PageLocaleEnum)),
      invalidateKeyOnSuccess: websitePagesQuery.getQueryKey(websiteId),
    })

  const handleChangeWebsiteLocale = (newWebsiteLocale: PageLocaleEnum) => {
    if (newWebsiteLocale !== websiteLocale) {
      fetcherUpdateWebsite({
        name: websiteName,
        defaultLocale: newWebsiteLocale,
        style: websiteStyles && transformWebsiteStylesForApi(websiteStyles),
        theme: websiteTheme && transformWebsiteTheme(websiteTheme),
      })
    }
  }

  const onOpenLogoSettings = () => {
    if (websiteLogoEntity) {
      // TODO: make sure it works
      dispatch(setEditingEntity(websiteLogoEntity.id))
    } else {
      // TODO: create website logo entity?
    }
    setActiveSidebarTab(null)
    showSidebar()
  }

  return (
    <>
      <span className="mb-6 block text-heading-4">
        {t('website_settings.sidebar.title')}
      </span>

      <TextInputControl
        value={websiteName}
        update={value => {
          dispatch(setWebsiteName(value))
        }}
        wrapperProps={{
          className: 'mb-6',
          labelText: t('website_settings.sidebar.website_name.label'),
          required: true,
        }}
      />

      <LanguageSelector
        isLoading={isLoadingUpdateWebsite}
        options={localesOptions}
        selectedLocales={websiteLocale as PageLocaleEnum}
        onChange={handleChangeWebsiteLocale}
        labelText={t('website_settings.sidebar.main_language.label')}
        required
      />

      <WebsiteLocalesSelector />

      <GroupTitle withoutHorizontalLine className="mb-2">
        website_settings.sidebar.logo.label
      </GroupTitle>
      <SettingsMenuButton onClick={onOpenLogoSettings}>
        {t('website_settings.sidebar.logo.nested_menu_title')}
      </SettingsMenuButton>
    </>
  )
}
